import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Trash2, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const { user } = useAuth();
  const { cartItems, removeFromCart, getCartTotal, loading } = useCart();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="text-center">
          <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Please login to view your cart
          </h2>
          <button
            onClick={() => navigate('/login')}
            className="bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A1A1A]"></div>
      </div>
    );
  }

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl mb-12"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="cart-title"
          >
            Shopping Cart
          </motion.h1>

          {cartItems.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-24"
            >
              <ShoppingBag size={64} className="mx-auto mb-6 text-[#888888]" strokeWidth={1} />
              <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your cart is empty
              </h2>
              <p className="text-[#888888] mb-8">Discover our beautiful handcrafted artwork</p>
              <button
                onClick={() => navigate('/featured')}
                data-testid="cart-browse-btn"
                className="bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300"
              >
                Browse Artwork
              </button>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-6">
                {cartItems.map((item, index) => (
                  <motion.div
                    key={item.product_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-6 rounded-lg border border-black/5 flex gap-6"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <img
                      src={item.product?.image_url}
                      alt={item.product?.name}
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="text-xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {item.product?.name}
                      </h3>
                      <p className="text-[#888888] text-sm mb-3">{item.product?.size}</p>
                      <p className="text-lg font-medium">${item.product?.price.toFixed(2)}</p>
                      <div className="mt-4">
                        <span className="text-sm text-[#888888]">Quantity: {item.quantity}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product_id)}
                      data-testid={`cart-remove-btn-${item.product_id}`}
                      className="text-[#888888] hover:text-[#1A1A1A] transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </motion.div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white p-8 rounded-lg border border-black/5 sticky top-32"
                >
                  <h3 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Order Summary
                  </h3>
                  <div className="space-y-4 mb-6 pb-6 border-b border-black/5">
                    <div className="flex justify-between">
                      <span className="text-[#888888]">Subtotal</span>
                      <span className="font-medium" data-testid="cart-subtotal">${getCartTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#888888]">Shipping</span>
                      <span className="font-medium">Calculated at checkout</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xl mb-8">
                    <span>Total</span>
                    <span className="font-bold" data-testid="cart-total">${getCartTotal().toFixed(2)}</span>
                  </div>
                  <button
                    onClick={handleCheckout}
                    data-testid="cart-checkout-btn"
                    className="w-full bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300"
                  >
                    Proceed to Checkout
                  </button>
                  <button
                    onClick={() => navigate('/featured')}
                    data-testid="cart-continue-shopping-btn"
                    className="w-full mt-4 bg-transparent border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300"
                  >
                    Continue Shopping
                  </button>
                </motion.div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
